import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import UseEmployeeHook from 'hooks/UseEmployeeHook';

const EmployeeTable = ({ employees, onEdit, onViewProfile, onResetPassword, onDeactivate, selectedEmployees, onSelectEmployee, onSelectAll }) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const {updateEmployee} = UseEmployeeHook();

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedEmployees = [...employees].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });
  // console.log(sortedEmployees)

 const getStatusBadge = (active) => {
  const statusConfig = active
    ? { color: 'text-success bg-success/10', label: 'Active' }
    : { color: 'text-warning bg-warning/10', label: 'Inactive' };

  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}
    >
      {statusConfig.label}
    </span>
  );
};

  const getRoleBadge = (role) => {
    const roleConfig = {
      manager: { color: 'text-primary bg-primary/10', label: 'Manager' },
      supervisor: { color: 'text-secondary bg-secondary/10', label: 'Supervisor' },
      employee: { color: 'text-muted-foreground bg-muted', label: 'Employee' },
      intern: { color: 'text-accent bg-accent/10', label: 'Intern' }
    };
    
    const config = roleConfig[role] || roleConfig.employee;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };
  

  const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD'
  }).format(amount);
};

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <div className="flex flex-col">
          <Icon 
            name="ChevronUp" 
            size={12} 
            className={`${sortField === field && sortDirection === 'asc' ? 'text-primary' : 'text-muted-foreground/50'}`}
          />
          <Icon 
            name="ChevronDown" 
            size={12} 
            className={`-mt-1 ${sortField === field && sortDirection === 'desc' ? 'text-primary' : 'text-muted-foreground/50'}`}
          />
        </div>
      </div>
    </th>
  );

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedEmployees.length === employees.length && employees.length > 0}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary"
                />
              </th>
              <SortableHeader field="name">Employee</SortableHeader>
              <SortableHeader field="email">Contact</SortableHeader>
              <SortableHeader field="role">Role</SortableHeader>
              <SortableHeader field="billingLimit">Total Sales</SortableHeader>
              <SortableHeader field="status">Status</SortableHeader>
              {/* <SortableHeader field="lastLogin">Last Login</SortableHeader> */}
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedEmployees.map((employee) => (
              <tr key={employee?._id} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedEmployees.includes(employee._id)}
                    onChange={(e) => onSelectEmployee(employee._id, e.target.checked)}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    {/* <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                      <Image
                        src={employee.avatar}
                        alt={employee.name}
                        className="w-full h-full object-cover"
                      />
                    </div> */}
                    <div>
                      <div className="text-sm font-medium text-foreground">{employee.name}</div>
                      {/* <div className="text-sm text-muted-foreground">ID: {employee?._id}</div> */}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-foreground">{employee.email}</div>
                  <div className="text-sm text-muted-foreground">{employee.mobile}</div>
                </td>
                <td className="px-6 py-4">
                  {getRoleBadge(employee.role)}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-foreground">
                    {formatCurrency(employee?.LastMonthSale)}
                  </div>
                  {/* <div className="text-xs text-muted-foreground">
                    Used: {formatCurrency(employee.billingUsed)}
                  </div> */}
                </td>
                <td className="px-6 py-4">
                 
                  {getStatusBadge(employee.active)}
                </td>
                {/* <td className="px-6 py-4">
                  <div className="text-sm text-foreground">
                    {formatDate(employee.lastLogin)}
                  </div>
                </td> */}
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewProfile(employee)}
                      className="h-8 w-8"
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(employee)}
                      className="h-8 w-8"
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    {/* <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onResetPassword(employee)}
                      className="h-8 w-8"
                    >
                      <Icon name="RotateCcw" size={16} />
                    </Button> */}
 {employee?.active == true ? (
                  <Button
                    variant="destructive"
                    size="sm"
                     onClick={ () => onDeactivate(employee, false) }

                    iconName="UserX"
                    iconSize={14}
                  >
                    Deactivate
                  </Button>
                ):(
                  <Button
                    variant="success"
                    size="sm"
                       onClick={ () => onDeactivate(employee, true) }
                    iconName="UserCheck"
                    iconSize={14}
                  >
                    Activate
                  </Button>
                )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4 p-4">
        {sortedEmployees.map((employee) => (
          <div key={employee.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedEmployees.includes(employee._id)}
                  onChange={(e) => onSelectEmployee(employee._id, e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary mt-1"
                />
                {/* <div className="w-12 h-12 rounded-full overflow-hidden bg-muted flex-shrink-0">
                  <Image
                    src={employee.avatar}
                    alt={employee.name}
                    className="w-full h-full object-cover"
                  />
                </div> */}
                <div>
                  <div className="text-sm font-medium text-foreground">{employee.name}</div>
                  <div className="text-xs text-muted-foreground">ID: {employee?._id}</div>
                  {getRoleBadge(employee.role)}
                </div>
              </div>
              {getStatusBadge(employee.active)}
            </div>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Mail" size={14} className="text-muted-foreground" />
                <span className="text-foreground">{employee.email}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Phone" size={14} className="text-muted-foreground" />
                <span className="text-foreground">{employee.phone}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="DollarSign" size={14} className="text-muted-foreground" />
                <span className="text-foreground">
                  {formatCurrency(employee?.LastMonthSale)} 
                </span>
              </div>
              {/* <div className="flex items-center space-x-2 text-sm">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-foreground">{formatDate(employee.lastLogin)}</span>
              </div> */}
            </div>
            
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewProfile(employee)}
                  iconName="Eye"
                  iconPosition="left"
                  iconSize={14}
                >
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(employee)}
                  iconName="Edit"
                  iconPosition="left"
                  iconSize={14}
                >
                  Edit
                </Button>
              </div>
              <div className="flex items-center space-x-2">
                {/* <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onResetPassword(employee)}
                  iconName="RotateCcw"
                  iconSize={14}
                >
                  Reset
                </Button> */}
                {employee?.active == true ? (
                  <Button
                    variant="destructive"
                    size="sm"
                     onClick={ () => onDeactivate(employee, false) }

                    iconName="UserX"
                    iconSize={14}
                  >
                    Deactivate
                  </Button>
                ):(
                  <Button
                    variant="success"
                    size="sm"
                       onClick={ () => onDeactivate(employee, true) }
                    iconName="UserCheck"
                    iconSize={14}
                  >
                    Activate
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmployeeTable;